// Voice system extracted from message-utils.js
// Refactored to use modular voice-settings, voice-cache, and voice-api modules

import { perkTierForUser } from '../../utils/membershipUsage.js';
import {
    persistVoiceLanguage,
    getCurrentVoiceId,
    getCurrentVoiceLanguage,
    getVoiceSettingsSnapshot,
    buildVoiceCompositeKey,
    buildVoiceReadyKey,
    getAudioCacheKey
} from './voice-settings.js';

import {
    cacheAudio,
    getCachedAudio,
    getCachedAudioChunks,
    cacheAudioChunks,
    clearStoredAudioForMessage,
    rekeyStoredAudioForMessage,
    AUDIO_CACHE_PREFIX,
    AUDIO_METADATA_PREFIX
} from './voice-cache.js';
import { encodeMessageKey } from './message-key-utils.js';
import { speakableText } from './speakable-text.js';

import {
    prepareVoiceRequestBody,
    callVoiceGenerationAPI,
    callStreamingVoiceGenerationAPI,
    isGreetingMessage,
    presentOpenRouterVoiceKeyError
} from './voice-api.js';
import { getAnalyticsTracker } from '../../analytics/hangout-obs-tracker.js';
import { showHangoutFreeAccountModal, showHangoutLoginModal } from './message-utils.js';
import { showVoiceAccessPricingModal } from '../../utils/quotaErrorHandler.js';
import {
    isOpenRouterVoiceByokEnabled,
    isOpenRouterVoiceByokKnown,
    refreshOpenRouterKeyStatus
} from '../../utils/openRouterKeyStatus.js';
import { maybeShowExpensiveVoiceToast } from './expensive-voice-toast.js';

export const Voice = (() => {
    // DI container
    /** @type {any} */
    let deps = null;
    let inited = false;

    // Internal state
    let _lastVoiceClickTime = 0;
    const _voiceClickDebounceMs = 500;
    let _voiceGenerationInProgress = false;
    const _pendingVoiceRequests = new Map();
    // Player map for Audio elements and AudioPlayerWithSeparation instances
    const audioPlayerMap = new Map();

    // Simple cache for generateAutoVoiceSync base64
    const voicePlayerCache = new Map();

    const init = (dependencies) => {
        if (inited) return;
        deps = dependencies;
        inited = true;
    };

    /**
     * Unified helper to set button background with mobile !important handling
     * @param {HTMLElement} button - Button element
     * @param {string} color - CSS color value
     */
    const setButtonBackground = (button, color) => {
        if (!button) return;
        if (button.id === 'mobile-voice-play-btn') {
            button.style.setProperty('background', color, 'important');
        } else {
            button.style.background = color;
        }
    };

    const resolveEmotionId = (characterId) => {
        const messages = deps.HangoutState.getInstance().getMessages() || [];
        const lastCharacterMessage = messages.slice().reverse().find(
            msg => msg.role === 'assistant' && String(msg.characterId) === String(characterId) && msg.actionId != null
        );
        return lastCharacterMessage?.emotionId || null;
    };

    /**
     * Find existing audio for a character, checking multiple sources
     * @param {string} characterId - Character ID
     * @param {string} messageText - Message text
     * @param {Object} voiceSettings - Voice settings
     * @param {string|null} currentVoiceKey - Current voice key from bubble
     * @returns {Object|null} Audio entry or null
     */
    const findExistingAudio = (characterId, messageText, voiceSettings, currentVoiceKey = null) => {
        let audioEntry = null;
        const settings = voiceSettings || getVoiceSettingsSnapshot(String(characterId));
        let expectedKey = null;
        try {
            if (messageText) {
                expectedKey = buildVoiceCompositeKey(characterId, messageText, settings);
            }
        } catch {
            expectedKey = null;
        }

        // Only reuse bubble.currentVoiceKey when it matches the active voice settings.
        if (currentVoiceKey && (!expectedKey || currentVoiceKey === expectedKey)) {
            audioEntry = getAudioEntryByKey(currentVoiceKey);
            if (audioEntry) return audioEntry;
        }

        // Try the standard lookup (keyed by current voice id + language)
        audioEntry = getCurrentAudioForCharacter(characterId, messageText, settings);
        if (audioEntry) return audioEntry;

        // Only return in-progress playback when it matches the expected composite key.
        if (expectedKey) {
            const entry = getAudioEntryByKey(expectedKey);
            if (entry && entry.audioPlayer && entry.audioPlayer.isPlaying) return entry;
            if (entry && entry.audio && !entry.audio.paused) return entry;
        }
        return null;
    };

    const normalizeAudioEntry = (key, value) => {
        if (!value) return null;
        if (typeof value.playWithSeparation === 'function') return { key, audioPlayer: value, audio: null };
        if (value.audio && typeof value.audio.play === 'function') return { key, audioPlayer: null, audio: value.audio };
        if (typeof value.play === 'function') return { key, audioPlayer: null, audio: value };
        return { key, audioPlayer: null, audio: null };
    };

    const getCurrentAudioForCharacter = (characterId, messageText = null, voiceSettings = null) => {
        if (!characterId) return null;
        const normalizedCharacterId = String(characterId);
        if (messageText) {
            const settings = voiceSettings || getVoiceSettingsSnapshot(normalizedCharacterId);
            const specificKey = buildVoiceCompositeKey(normalizedCharacterId, messageText, settings);
            if (audioPlayerMap.has(specificKey)) {
                return normalizeAudioEntry(specificKey, audioPlayerMap.get(specificKey));
            }
            if (!voiceSettings) {
                const messageKey = deps.encodeMessageKey(messageText);
                const messagePrefix = `${normalizedCharacterId}::${messageKey}::`;
                for (const [key, audioPlayer] of audioPlayerMap.entries()) {
                    if (key.startsWith(messagePrefix)) return normalizeAudioEntry(key, audioPlayer);
                }
            }
        }
        if (voiceSettings) return null;
        const characterPrefix = `${normalizedCharacterId}::`;
        for (const [key, audioPlayer] of audioPlayerMap.entries()) {
            if (key.startsWith(characterPrefix)) return normalizeAudioEntry(key, audioPlayer);
        }
        return null;
    };

    const getAudioEntryByKey = (audioKey) => {
        if (!audioKey) return null;
        if (!audioPlayerMap.has(audioKey)) return null;
        return normalizeAudioEntry(audioKey, audioPlayerMap.get(audioKey));
    };

    const base64ToUint8Array = (base64) => {
        const audioBytes = atob(base64);
        const audioArray = new Uint8Array(audioBytes.length);
        for (let i = 0; i < audioBytes.length; i++) audioArray[i] = audioBytes.charCodeAt(i);
        return audioArray;
    };

    const resetVoicePlayButtons = () => {
        document.querySelectorAll('.voice-play-btn').forEach(button => {
            /** @type {HTMLElement} */ (button).innerHTML = '<i class="fas fa-play"></i>';
            /** @type {HTMLElement} */ (button).title = 'Play voice';
            /** @type {HTMLButtonElement} */ (button).disabled = false;
            setButtonBackground(/** @type {HTMLElement} */ (button), 'rgba(255, 255, 255, 0.9)');
        });
    };

    const restoreVoiceButtonState = (bubble, characterId, messageText) => {
        if (!bubble || !characterId || !messageText) return;
        try {
            const voiceSettings = getVoiceSettingsSnapshot(String(characterId));
            const cacheKey = getAudioCacheKey(characterId, messageText, voiceSettings);
            const cachedChunks = getCachedAudioChunks(cacheKey);
            const voiceStateKey = buildVoiceReadyKey(characterId, messageText, voiceSettings);
            const voiceState = deps.storageMechanism.getItem(voiceStateKey);
            if (cachedChunks || voiceState === 'ready') {
                if (cachedChunks) {
                    bubble.setVoiceButtonState('loading');
                    deps.displayManager.redrawAllMessages();
                    
                    const audioPlayer = new deps.AudioPlayerWithSeparation(characterId, messageText);
                    const audioKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
                    audioPlayerMap.set(audioKey, audioPlayer);
                    /** @type {any} */ (audioPlayer).preparePromise = audioPlayer.prepareAudio(cachedChunks.audioBuffer, cachedChunks.metadata.chunkTimings, cachedChunks.metadata.separationMs).then(() => {
                        bubble.setVoiceButtonState('ready');
                        deps.displayManager.redrawAllMessages();
                    }).catch(error => {
                        console.warn('[Voice Restore] Preparation failed:', error);
                        bubble.voiceButtonState = 'play';
                        deps.displayManager.redrawAllMessages();
                    });
                } else {
                    bubble.voiceButtonState = 'play';
                }
            } else {
                bubble.voiceButtonState = 'play';
            }
        } catch (error) {
            console.warn('Error restoring voice button state:', error);
            bubble.voiceButtonState = 'play';
        }
    };

    const createAudioFromResponse = (responseData, characterId, messageText, cacheKey, bubble = null, voiceSettings = null, audioKeyOverride = null) => {
        let audio;
        let audioUrl = null;
        let audioPlayer = null;
        const settings = voiceSettings || getVoiceSettingsSnapshot(String(characterId));
        const audioMapKey = audioKeyOverride || buildVoiceCompositeKey(characterId, messageText, settings);
        const chunkSeparationEnabled = deps.storage.get('chunkSeparationEnabled') !== 'false';
        const serverSeparationMs = 500;
        if (responseData.chunkTimings && (responseData.audioBuffer || responseData.audioUrl) && chunkSeparationEnabled) {
            const isStreamingResponse = responseData.streaming_used || responseData.type === 'complete';
            let audioBuffer;
            let audioBufferPromise = null;
            if (responseData.audioUrl && !responseData.audioBuffer) {
                if (typeof responseData.audioUrl !== 'string') {
                    console.error('Invalid audioUrl type:', typeof responseData.audioUrl, responseData.audioUrl);
                    throw new Error('Invalid audioUrl: expected string, got ' + typeof responseData.audioUrl);
                }
                const preloadKey = `${characterId}-${messageText}-${responseData.audioUrl}`;
                const fullUrl = responseData.audioUrl.startsWith('http') ? responseData.audioUrl : `${deps.GROK_WORKER_URL}${responseData.audioUrl}`;
                if (isStreamingResponse) { /* noop */ }
                audioBufferPromise = deps.audioPreloader.preload(fullUrl, preloadKey);
            }
            audioPlayer = new deps.AudioPlayerWithSeparation(characterId, messageText);
            audioPlayer.preparePromise = null;
            if (responseData.audioBuffer) {
                if (typeof responseData.audioBuffer === 'string') {
                    const audioArray = base64ToUint8Array(responseData.audioBuffer);
                    audioBuffer = audioArray.buffer;
                } else {
                    audioBuffer = responseData.audioBuffer;
                }
            }
            if (audioBuffer) {
                audioPlayer.prepareAudio(audioBuffer, responseData.chunkTimings, serverSeparationMs);
                audioPlayer.preparePromise = Promise.resolve(audioBuffer);
            } else if (audioBufferPromise) {
                audioPlayer.preparePromise = audioBufferPromise.then(buffer => {
                    audioPlayer.prepareAudio(buffer, responseData.chunkTimings, serverSeparationMs);
                    try { cacheAudioChunks(cacheKey, buffer, responseData.chunkTimings, serverSeparationMs); } catch (e) { console.warn('Failed to cache streaming chunks:', e); }
                    return buffer;
                }).catch(error => {
                    console.warn('Background audio preparation failed:', error);
                    throw error;
                });
            }
            audio = {
                get paused() {
                    return !audioPlayer.isPlaying || audioPlayer._cancelRequested;
                },
                set paused(value) {
                    if (value && audioPlayer.isPlaying) audioPlayer.pause();
                },
                play: async () => {
                    if (audioPlayer.isPlaying && !audioPlayer._cancelRequested) return;
                    audioPlayer._cancelRequested = false;
                    if (bubble) {
                        audioPlayer.onPlay = () => { bubble.setVoiceButtonState('playing'); bubble.isAudioPlaying = true; };
                        audioPlayer.onPause = () => { bubble.setVoiceButtonState('ready'); bubble.isAudioPlaying = false; };
                    }
                    if (audioBuffer) {
                        await audioPlayer.playWithSeparation(audioBuffer, responseData.chunkTimings, serverSeparationMs);
                        return;
                    }
                    if (!audioBuffer && audioBufferPromise) {
                        try {
                            audioBuffer = await audioBufferPromise;
                            audioPlayer.preparePromise = Promise.resolve(audioBuffer);
                        } catch (error) {
                            console.error('Failed to get preloaded buffer, falling back:', error);
                            const audioSrc = responseData.audioUrl.startsWith('http') ? responseData.audioUrl : `${deps.GROK_WORKER_URL}${responseData.audioUrl}`;
                            const fallbackAudio = new Audio(audioSrc);
                            await fallbackAudio.play();
                            return;
                        }
                    } else if (!audioBuffer && responseData.audioUrl) {
                        try {
                            const audioSrc = responseData.audioUrl.startsWith('http') ? responseData.audioUrl : `${deps.GROK_WORKER_URL}${responseData.audioUrl}`;
                            const audioResponse = await fetch(audioSrc);
                            audioBuffer = await audioResponse.arrayBuffer();
                            audioPlayer.preparePromise = Promise.resolve(audioBuffer);
                        } catch (error) {
                            console.error('Failed to fetch buffer, falling back:', error);
                            const audioSrc = responseData.audioUrl.startsWith('http') ? responseData.audioUrl : `${deps.GROK_WORKER_URL}${responseData.audioUrl}`;
                            const fallbackAudio = new Audio(audioSrc);
                            await fallbackAudio.play();
                            return;
                        }
                    }
                    if (bubble) {
                        audioPlayer.onPlay = () => { bubble.setVoiceButtonState('playing'); bubble.isAudioPlaying = true; };
                        audioPlayer.onPause = () => { bubble.setVoiceButtonState('ready'); bubble.isAudioPlaying = false; };
                    }
                    await audioPlayer.playWithSeparation(audioBuffer, responseData.chunkTimings, serverSeparationMs);
                },
                pause: () => { audioPlayer.pause(); },
                stop: () => { audioPlayer.stop(); },
                currentTime: 0,
                addEventListener: (event, callback) => {
                    if (event === 'play') audioPlayer.onPlay = callback;
                    else if (event === 'pause') audioPlayer.onPause = callback;
                    else if (event === 'ended') audioPlayer.onEnded = callback;
                    else if (event === 'error') audioPlayer.onError = callback;
                }
            };
        } else {
            if (responseData.audioUrl) {
                if (typeof responseData.audioUrl !== 'string') {
                    console.error('Invalid audioUrl type in legacy format:', typeof responseData.audioUrl, responseData.audioUrl);
                    throw new Error('Invalid audioUrl: expected string, got ' + typeof responseData.audioUrl);
                }
                const audioSrc = responseData.audioUrl.startsWith('http') ? responseData.audioUrl : `${deps.GROK_WORKER_URL}${responseData.audioUrl}`;
                audio = new Audio(audioSrc);
            } else if (responseData.audio) {
                cacheAudio(cacheKey, responseData.audio);
                const audioArray = base64ToUint8Array(responseData.audio);
                const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
                audio = new Audio();
                audioUrl = URL.createObjectURL(audioBlob);
                audio.src = audioUrl;
                audio.addEventListener('ended', () => URL.revokeObjectURL(audioUrl));
                audio.addEventListener('error', () => URL.revokeObjectURL(audioUrl));
            } else {
                throw new Error('No audio data received from server');
            }
        }
        if (audioPlayer) {
            audioPlayerMap.set(audioMapKey, audioPlayer);
            if (responseData.chunkTimings && responseData.audioBuffer) {
                let bufferToCache = responseData.audioBuffer;
                if (typeof responseData.audioBuffer === 'string') {
                    const audioArray = base64ToUint8Array(responseData.audioBuffer);
                    bufferToCache = audioArray.buffer;
                }
                cacheAudioChunks(cacheKey, bufferToCache, responseData.chunkTimings, 500);
            }
        } else if (audio) {
            audioPlayerMap.set(audioMapKey, { audio });
        }
        return { audio, audioUrl, audioPlayer };
    };

    const setupDOMButtonAudioListeners = (audio, button, audioKey, audioUrl = null, characterColor = 'var(--color-error)') => {
        if (!button) return;
        const existing = audioPlayerMap.get(audioKey);
        if (existing && existing !== audio && existing.isPlaying) {
            existing.pause?.();
        }

        audio.addEventListener('play', () => {
            button.innerHTML = '<i class="fas fa-pause"></i>';
            button.title = 'Pause voice';
            setButtonBackground(button, deps.hexToRgba(characterColor, 0.9));
            button.disabled = false;
        });
        audio.addEventListener('pause', () => {
            button.innerHTML = '<i class="fas fa-play"></i>';
            button.title = 'Resume voice';
            setButtonBackground(button, 'rgba(255, 255, 255, 0.9)');
        });
        audio.addEventListener('ended', () => {
            audio.currentTime = 0;
            button.innerHTML = '<i class="fas fa-play"></i>';
            button.title = 'Play voice';
            button.disabled = false;
            setButtonBackground(button, deps.hexToRgba(characterColor, 0.7));
        });
        audio.addEventListener('error', (e) => {
            console.error('Audio playback error:', e);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            audioPlayerMap.delete(audioKey);
            button.innerHTML = '<i class="fas fa-exclamation-triangle"></i>';
            button.title = 'Voice error - click to retry';
            button.disabled = false;
            setButtonBackground(button, 'rgba(255, 69, 0, 0.9)');
        });
    };

    const createAudioFromBase64 = (base64Audio, characterId, messageText, button, voiceSettings = null, audioKeyOverride = null) => {
        const audioArray = base64ToUint8Array(base64Audio);
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio();
        audio.src = audioUrl;
        const settings = voiceSettings || getVoiceSettingsSnapshot(String(characterId));
        const audioKey = audioKeyOverride || buildVoiceCompositeKey(characterId, messageText, settings);

        audio.addEventListener('play', () => {
            if (button) {
                button.innerHTML = '<i class="fas fa-pause"></i>';
                button.title = 'Pause voice';
                setButtonBackground(button, 'rgba(255, 215, 0, 0.9)');
                button.disabled = false;
            }
        });
        audio.addEventListener('pause', () => {
            if (button) {
                button.innerHTML = '<i class="fas fa-play"></i>';
                button.title = 'Resume voice';
                setButtonBackground(button, 'rgba(255, 255, 255, 0.9)');
            }
        });
        audio.addEventListener('ended', () => {
            audio.currentTime = 0;
            if (button) {
                button.innerHTML = '<i class="fas fa-play"></i>';
                button.title = 'Play voice';
                button.disabled = false;
                setButtonBackground(button, 'rgba(255, 255, 255, 0.9)');
            }
        });
        audio.addEventListener('error', () => {
            URL.revokeObjectURL(audioUrl);
            audioPlayerMap.delete(audioKey);
        });
        return audio;
    };

    const stopAllAudio = () => {
        audioPlayerMap.forEach((audioPlayer) => {
            if (audioPlayer.isPlaying) {
                audioPlayer.pause();
            }
        });
        audioPlayerMap.clear();
        resetVoicePlayButtons();
        deps.displayManager.getActiveMessages().forEach(bubble => {
            if (bubble.voiceButtonState !== 'play') {
                bubble.voiceButtonState = 'play';
                bubble.isAudioPlaying = false;
            }
        });
        deps.displayManager.redrawAllMessages();
    };

    const stopAllAudioGlobally = () => {
        audioPlayerMap.forEach((audioPlayer) => {
            if (audioPlayer && (audioPlayer.isPlaying || audioPlayer._playLock)) {
                audioPlayer.pause();
                audioPlayer.isPlaying = false;
                audioPlayer._playLock = false;
                audioPlayer._cancelRequested = false;
            }
        });
        document.querySelectorAll('audio').forEach(audio => { if (!audio.paused) audio.pause(); });
        deps.displayManager.getActiveMessages().forEach(bubble => {
            if (bubble.isAudioPlaying || bubble.voiceButtonState === 'playing') {
                bubble.setVoiceButtonState('ready');
                bubble.isAudioPlaying = false;
            }
        });
        resetVoicePlayButtons();
        if (window.preGeneratedAudioMap) {
            window.preGeneratedAudioMap.forEach((audioData) => {
                if (audioData && audioData.audio && !audioData.audio.paused) {
                    audioData.audio.pause();
                }
            });
        }
    };

    /**
     * Drop in-memory cached audio for a character after the active voice changes,
     * and reset message voice-button ready state so the next click regenerates.
     * @param {string|number} characterId
     * @param {string|null} [keepVoiceId] - when set, keep map entries for this voice id
     */
    const invalidateCachedVoiceForCharacter = (characterId, keepVoiceId = null) => {
        if (characterId == null) return;
        const normalizedCharacterId = String(characterId);
        const characterPrefix = `${normalizedCharacterId}::`;
        const keepVoicePart = keepVoiceId && String(keepVoiceId).trim() !== ''
            ? String(keepVoiceId).trim()
            : null;

        for (const [key, audioPlayer] of [...audioPlayerMap.entries()]) {
            if (!key.startsWith(characterPrefix)) continue;
            if (keepVoicePart) {
                const parts = key.split('::');
                const keyVoiceId = parts[parts.length - 1];
                if (keyVoiceId === keepVoicePart) continue;
            }
            if (audioPlayer?.isPlaying || audioPlayer?._playLock) {
                audioPlayer.pause?.();
                audioPlayer.isPlaying = false;
                audioPlayer._playLock = false;
            }
            if (audioPlayer?.audio && !audioPlayer.audio.paused) {
                audioPlayer.audio.pause();
            }
            audioPlayerMap.delete(key);
        }

        for (const [requestKey] of [..._pendingVoiceRequests.entries()]) {
            if (requestKey.startsWith(characterPrefix)) {
                _pendingVoiceRequests.delete(requestKey);
            }
        }

        const messages = deps?.displayManager?.getActiveMessages?.() || [];
        for (const bubble of messages) {
            if (String(bubble?.characterId) !== normalizedCharacterId) continue;
            bubble.currentVoiceKey = null;
            bubble.isAudioPlaying = false;
            if (typeof bubble.setVoiceButtonState === 'function') {
                bubble.setVoiceButtonState('play');
            } else {
                bubble.voiceButtonState = 'play';
                bubble.markDirty?.();
            }
        }
        deps?.displayManager?.redrawAllMessages?.();
    };

    const stopMappedAudio = (audioPlayer) => {
        if (!audioPlayer) return;
        if (audioPlayer.isPlaying || audioPlayer._playLock) {
            audioPlayer.pause?.();
            audioPlayer.isPlaying = false;
            audioPlayer._playLock = false;
        }
        if (audioPlayer.audio && !audioPlayer.audio.paused) {
            audioPlayer.audio.pause();
        }
    };

    const rekeyCompositeMap = (map, oldPrefix, newPrefix) => {
        for (const [key, value] of [...map.entries()]) {
            if (!key.startsWith(oldPrefix)) continue;
            const renamed = newPrefix + key.slice(oldPrefix.length);
            map.delete(key);
            if (!map.has(renamed)) map.set(renamed, value);
        }
    };

    const resetEditedBubbleVoice = (bubble) => {
        if (!bubble) return;
        bubble.currentVoiceKey = null;
        bubble.isAudioPlaying = false;
        if (typeof bubble.setVoiceButtonState === 'function') {
            bubble.setVoiceButtonState('play');
        } else {
            bubble.voiceButtonState = 'play';
            bubble.markDirty?.();
        }
    };

    /**
     * After an assistant edit: drop every cached voice for the previous text when
     * the speakable line changed. Narration-only edits keep the audio and retarget
     * it at the new full-text cache key.
     */
    const syncCachedVoiceAfterMessageEdit = (characterId, previousText, nextText, bubble = null) => {
        if (characterId == null) return;
        const previousMessageKey = encodeMessageKey(previousText);
        const nextMessageKey = encodeMessageKey(nextText);
        if (speakableText(previousText) === speakableText(nextText)) {
            if (previousMessageKey === nextMessageKey) return;
            const oldPrefix = `${String(characterId)}::${previousMessageKey}::`;
            const newPrefix = `${String(characterId)}::${nextMessageKey}::`;
            rekeyStoredAudioForMessage(characterId, previousMessageKey, nextMessageKey);
            rekeyCompositeMap(audioPlayerMap, oldPrefix, newPrefix);
            rekeyCompositeMap(voicePlayerCache, oldPrefix, newPrefix);
            rekeyCompositeMap(_pendingVoiceRequests, oldPrefix, newPrefix);
            if (bubble?.currentVoiceKey && String(bubble.currentVoiceKey).startsWith(oldPrefix)) {
                bubble.currentVoiceKey = newPrefix + String(bubble.currentVoiceKey).slice(oldPrefix.length);
            }
            return;
        }

        const oldPrefix = `${String(characterId)}::${previousMessageKey}::`;
        clearStoredAudioForMessage(characterId, previousMessageKey);
        for (const [key, audioPlayer] of [...audioPlayerMap.entries()]) {
            if (!key.startsWith(oldPrefix)) continue;
            stopMappedAudio(audioPlayer);
            audioPlayerMap.delete(key);
        }
        for (const [key] of [...voicePlayerCache.entries()]) {
            if (key.startsWith(oldPrefix)) voicePlayerCache.delete(key);
        }
        for (const [key] of [..._pendingVoiceRequests.entries()]) {
            if (key.startsWith(oldPrefix)) _pendingVoiceRequests.delete(key);
        }
        resetEditedBubbleVoice(bubble);
    };

    const setupAutoVoiceEventListeners = (audio, characterId, messageText, characterName, audioUrl = null) => {
        const voiceSettings = getVoiceSettingsSnapshot(String(characterId));
        const audioKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
        const existing = audioPlayerMap.get(audioKey);
        if (existing && existing !== audio && existing.isPlaying) {
            existing.pause?.();
        }

        const mobileVoiceBtn = document.getElementById('mobile-voice-play-btn');
        if (mobileVoiceBtn) {
            mobileVoiceBtn.dataset.characterId = characterId;
            mobileVoiceBtn.dataset.messageText = messageText;
            mobileVoiceBtn.dataset.voiceKey = audioKey;
        }

        const allVoiceButtons = document.querySelectorAll(`.voice-play-btn[data-character-id="${characterId}"]`);
        const buttonsForCharacter = Array.from(allVoiceButtons);
        if (mobileVoiceBtn && mobileVoiceBtn.dataset.characterId === String(characterId)) {
            buttonsForCharacter.push(mobileVoiceBtn);
        }
        const canvasBubble = deps.displayManager.getActiveMessages().find(bubble => bubble.characterId === characterId);
        if (canvasBubble) {
            canvasBubble.currentVoiceKey = audioKey;
        }
        const characterColor = canvasBubble?.characterColor || 'var(--color-error)';

        const updateButtons = (state) => {
            buttonsForCharacter.forEach(voiceButton => {
                if (!voiceButton) return;
                /** @type {HTMLElement} */
                const buttonEl = /** @type {HTMLElement} */ (voiceButton);
                if (state === 'play') {
                    buttonEl.innerHTML = '<i class="fas fa-pause"></i>';
                    buttonEl.title = 'Pause voice';
                    setButtonBackground(buttonEl, deps.hexToRgba(characterColor, 0.9));
                    /** @type {HTMLButtonElement} */ (buttonEl).disabled = false;
                } else if (state === 'pause') {
                    buttonEl.innerHTML = '<i class="fas fa-play"></i>';
                    buttonEl.title = 'Resume voice';
                    setButtonBackground(buttonEl, 'rgba(255, 255, 255, 0.9)');
                } else if (state === 'ended') {
                    buttonEl.innerHTML = '<i class="fas fa-play"></i>';
                    buttonEl.title = 'Play voice';
                    /** @type {HTMLButtonElement} */ (buttonEl).disabled = false;
                    setButtonBackground(buttonEl, deps.hexToRgba(characterColor, 0.7));
                } else if (state === 'error') {
                    buttonEl.innerHTML = '<i class="fas fa-exclamation-triangle"></i>';
                    buttonEl.title = 'Voice error - click to retry';
                    /** @type {HTMLButtonElement} */ (buttonEl).disabled = false;
                    setButtonBackground(buttonEl, 'rgba(255, 69, 0, 0.9)');
                } else if (state === 'loading') {
                    buttonEl.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                    buttonEl.title = 'Loading voice...';
                    /** @type {HTMLButtonElement} */ (buttonEl).disabled = true;
                    setButtonBackground(buttonEl, 'rgba(255, 255, 255, 0.9)');
                } else if (state === 'ready') {
                    buttonEl.innerHTML = '<i class="fas fa-play"></i>';
                    buttonEl.title = 'Voice ready to play';
                    /** @type {HTMLButtonElement} */ (buttonEl).disabled = false;
                    setButtonBackground(buttonEl, 'rgba(0, 255, 0, 0.6)');
                }
            });
        };

        updateButtons('ended');

        audio.addEventListener('play', () => {
            updateButtons('play');
            if (canvasBubble) {
                canvasBubble.voiceButtonState = 'playing';
                canvasBubble.isAudioPlaying = true;
                deps.displayManager.redrawAllMessages();
            }
        });
        audio.addEventListener('pause', () => {
            updateButtons('pause');
            if (canvasBubble) {
                canvasBubble.voiceButtonState = 'play';
                canvasBubble.isAudioPlaying = false;
                deps.displayManager.redrawAllMessages();
            }
        });
        audio.addEventListener('ended', () => {
            audio.currentTime = 0;
            updateButtons('ended');
            if (canvasBubble) {
                canvasBubble.voiceButtonState = 'ready';
                canvasBubble.isAudioPlaying = false;
                deps.displayManager.redrawAllMessages();
            }
            const audioEntry = audioPlayerMap.get(audioKey);
            if (audioEntry) {
                audioEntry.isPlaying = false;
                audioEntry._playLock = false;
                audioEntry._cancelRequested = false;
                audioEntry.currentTime = 0;
                audioEntry._lastPlayTime = 0;
                audioEntry._isPaused = false;
                audioEntry._resumeSeconds = undefined;
                audioEntry._currentChunkIndex = 0;
                audioEntry._chunkOffset = 0;
                audioEntry._simpleOffset = 0;
            }
        });
        audio.addEventListener('error', (e) => {
            console.error('Auto voice audio playback error:', e);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            audioPlayerMap.delete(audioKey);
            updateButtons('error');
            if (canvasBubble) {
                canvasBubble.voiceButtonState = 'error';
                canvasBubble.isAudioPlaying = false;
                deps.displayManager.redrawAllMessages();
            }
        });
    };

    const cleanupStalePendingRequests = () => {
        const now = Date.now();
        for (const [key, promise] of _pendingVoiceRequests.entries()) {
            const ts = /** @type {any} */ (promise)._timestamp || 0;
            if (now - ts > 60000) _pendingVoiceRequests.delete(key);
        }
    };

    const addVoicePlayListener = (bubble, characterId, messageText) => {
        const voiceBtn = bubble.querySelector('.voice-play-btn');
        if (!voiceBtn) return;
        voiceBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            let buttonMessageText = voiceBtn.getAttribute('data-message-text') || messageText;
            if (buttonMessageText) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = buttonMessageText;
                buttonMessageText = tempDiv.textContent || tempDiv.innerText || buttonMessageText;
            }
            if (!buttonMessageText || buttonMessageText.trim() === '') {
                console.warn('Voice button: No message text – cannot generate new audio, doing nothing');
                return;
            }
            const voiceSettings = getVoiceSettingsSnapshot(String(characterId));
            trackVoiceAnalyticsEvent('voice_try', characterId, voiceSettings, {
                source: 'bubble_voice_button'
            });
            const currentAudioEntry = findExistingAudio(characterId, buttonMessageText, voiceSettings, bubble.currentVoiceKey);

            if (currentAudioEntry) {
                if (currentAudioEntry.audio) {
                    const { audio } = currentAudioEntry;
                    if (audio.paused) {
                        try {
                            await audio.play();
                            voiceBtn.innerHTML = '<i class="fas fa-pause"></i>';
                            voiceBtn.title = 'Pause voice';
                            const characterColor = await deps.getCharacterColor(characterId);
                            voiceBtn.style.background = deps.hexToRgba(characterColor, 0.9);
                            trackVoiceAnalyticsEvent('voice_success', characterId, voiceSettings, {
                                source: 'existing_bubble_audio'
                            });
                        } catch (err) {
                            console.error('Error resuming audio:', err);
                            trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                                failure_reason: 'existing_bubble_audio_play_failed'
                            });
                        }
                    } else {
                        audio.pause();
                        voiceBtn.innerHTML = '<i class="fas fa-play"></i>';
                        voiceBtn.title = 'Resume voice';
                        voiceBtn.style.background = 'rgba(255, 255, 255, 0.9)';
                    }
                    return;
                }
                if (currentAudioEntry.audioPlayer) {
                    const { audioPlayer } = currentAudioEntry;
                    if (audioPlayer.isPlaying) {
                        audioPlayer.pause();
                        voiceBtn.innerHTML = '<i class="fas fa-play"></i>';
                        voiceBtn.title = 'Resume voice';
                        voiceBtn.style.background = 'rgba(255, 255, 255, 0.9)';
                        return;
                    }
                    if (audioPlayer.audioBuffer && audioPlayer.chunkTimings) {
                        try {
                            stopAllAudioGlobally();
                            voiceBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                            voiceBtn.disabled = true;
                            await audioPlayer.playWithSeparation(
                                audioPlayer.audioBuffer,
                                audioPlayer.chunkTimings,
                                audioPlayer.separationMs || 500
                            );
                            trackVoiceAnalyticsEvent('voice_success', characterId, voiceSettings, {
                                source: 'existing_bubble_audio_player'
                            });
                            return;
                        } catch (err) {
                            console.warn('AudioPlayerWithSeparation replay failed, regenerating voice:', err);
                            trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                                failure_reason: 'existing_bubble_audio_player_failed'
                            });
                        } finally {
                            voiceBtn.disabled = false;
                        }
                    }
                }
            }
            const messageId = await findMessageIdByText(characterId, buttonMessageText);
            await playVoice(characterId, buttonMessageText, voiceBtn, messageId, true, voiceSettings);
        });
    };

    const findMessageIdByText = async (characterId, text) => {
        if (deps && typeof deps.findMessageIdByText === 'function') {
            return await deps.findMessageIdByText(characterId, text);
        }
        const globalFindMessageIdByText = (/** @type {any} */ (window)).findMessageIdByText;
        if (typeof globalFindMessageIdByText === 'function') {
            return await globalFindMessageIdByText(characterId, text);
        }
        console.warn('findMessageIdByText not available, voice generation may fail');
        return null;
    };

    const isFreeVoicePaidByOpenRouter = async () => {
        if (!isOpenRouterVoiceByokKnown()) {
            await refreshOpenRouterKeyStatus();
        }
        return isOpenRouterVoiceByokEnabled();
    };

    const showVoiceAccessModal = async (context = 'voice-access', bubble = null) => {
        const userData = deps.storage.get('userData') || {};
        const authToken = (await window.tokenManager?.getValidAccessToken?.() ?? '') || '';
        const isAuthenticated = !!authToken && (userData?.isLoggedIn === true || window.tokenManager?.isAuthenticated?.());
        const currentTier = perkTierForUser(userData);

        if (!isAuthenticated) {
            await showHangoutLoginModal(context, 'Please log in to use voice features');
            return;
        }

        if (currentTier === 'free') {
            await showVoiceAccessPricingModal({
                currentTier,
                characterId: bubble?.characterId ?? null
            });
            return;
        }

        await showHangoutFreeAccountModal(context);
    };

    const trackVoiceAnalyticsEvent = (eventType) => {
        if (eventType !== 'voice_try') return;
        try {
            getAnalyticsTracker().trackFeature('voice_used');
        } catch (error) {
            console.warn('[Voice Analytics] Failed to track voice use:', error);
        }
    };

    const handleVoiceButtonClick = async (bubble) => {
        let requestKey;
        const voiceSettings = getVoiceSettingsSnapshot(String(bubble.characterId));
        trackVoiceAnalyticsEvent('voice_try', bubble.characterId, voiceSettings, {
            source: 'vn_voice_button'
        });
        try {
            const authToken = (await window.tokenManager?.getValidAccessToken?.() ?? '') || '';
            const isGreeting = deps.isGreetingMessage(bubble.characterId);
            if (!authToken && !isGreeting) {
                await showVoiceAccessModal('voice-unauthenticated', bubble);
                bubble.setVoiceButtonState('play');
                bubble.isAudioPlaying = false;
                deps.displayManager.redrawAllMessages();
                trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                    failure_reason: 'authentication_required'
                });
                return;
            }

            try {
                requestKey = buildVoiceCompositeKey(bubble.characterId, bubble.text, voiceSettings);
            } catch (error) {
                console.error('[Voice Button Debug] ERROR building requestKey:', error);
                trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                    failure_reason: 'request_key_build_failed'
                });
                return;
            }
            cleanupStalePendingRequests();
            
            const audioEntry = findExistingAudio(bubble.characterId, bubble.text, voiceSettings, bubble.currentVoiceKey);

            // IMPORTANT UX: if audio already exists, allow immediate play/pause without debounce.
            // Debounce should only guard NEW generation starts.

            if (audioEntry && audioEntry.audioPlayer) {
                bubble.currentVoiceKey = requestKey;
                const { audioPlayer } = audioEntry;

                if (audioPlayer.isPlaying || bubble.voiceButtonState === 'playing') {
                    audioPlayer.pause();
                    bubble.setVoiceButtonState('ready');
                    bubble.isAudioPlaying = false;
                    bubble.currentVoiceKey = null;
                    deps.displayManager.redrawAllMessages();
                    return;
                } else {
                    stopAllAudioGlobally();
                    
                    if (bubble) {
                        audioEntry.audioPlayer.onPlay = () => { bubble.setVoiceButtonState('playing'); bubble.isAudioPlaying = true; deps.displayManager.redrawAllMessages(); };
                        audioEntry.audioPlayer.onPause = () => { bubble.setVoiceButtonState('ready'); bubble.isAudioPlaying = false; deps.displayManager.redrawAllMessages(); };
                        audioEntry.audioPlayer.onEnded = () => { bubble.setVoiceButtonState('ready'); bubble.isAudioPlaying = false; deps.displayManager.redrawAllMessages(); };
                    }
                    
                    if (audioPlayer.preparePromise && !audioPlayer.isPrepared) {
                        bubble.setVoiceButtonState('loading');
                        deps.displayManager.redrawAllMessages();
                        try {
                            await audioPlayer.preparePromise;
                        } catch (error) {
                            console.warn('AudioPlayer preparation failed, regenerating...', error);
                        }
                    }
                    
                    if (audioPlayer.audioBuffer && audioPlayer.chunkTimings && audioPlayer.isPrepared) {
                        await audioPlayer.playWithSeparation(audioPlayer.audioBuffer, audioPlayer.chunkTimings, audioPlayer.separationMs || 500);
                        trackVoiceAnalyticsEvent('voice_success', bubble.characterId, voiceSettings, {
                            source: 'existing_audio_player'
                        });
                    } else {
                        bubble.setVoiceButtonState('loading');
                        deps.displayManager.redrawAllMessages();
                        _voiceGenerationInProgress = true;
                        const messageId = await findMessageIdByText(bubble.characterId, bubble.text);
                        const voicePromise = playVoiceForBubble(bubble.characterId, bubble.text, bubble, messageId, voiceSettings, requestKey);
                        /** @type {any} */ (voicePromise)._timestamp = Date.now();
                        _pendingVoiceRequests.set(requestKey, voicePromise);
                        try { await voicePromise; } finally { _voiceGenerationInProgress = false; _pendingVoiceRequests.delete(requestKey); }
                    }
                }
            } else if (audioEntry && audioEntry.audio) {
                // Non-chunked audio path: we stored a plain HTMLAudioElement in the map.
                // Support the same play/pause UX so clicking the voice button actually plays.
                bubble.currentVoiceKey = audioEntry.key || requestKey;
                const audio = audioEntry.audio;

                // If it's currently playing, pause it.
                if (!audio.paused || bubble.voiceButtonState === 'playing') {
                    audio.pause();
                    bubble.setVoiceButtonState('ready');
                    bubble.isAudioPlaying = false;
                    deps.displayManager.redrawAllMessages();
                    return;
                }

                stopAllAudioGlobally();
                setupBubbleAudioListeners(audio, bubble, bubble.currentVoiceKey);

                // Mark "ready" and attempt playback. If the browser blocks autoplay (lost gesture),
                // user can click again; we keep the button armed.
                bubble.setVoiceButtonState('ready');
                deps.displayManager.redrawAllMessages();
                if (audio.ended || audio.currentTime >= (audio.duration || 0)) {
                    audio.currentTime = 0;
                }
                try {
                    await audio.play();
                    trackVoiceAnalyticsEvent('voice_success', bubble.characterId, voiceSettings, {
                        source: 'existing_audio'
                    });
                } catch (error) {
                    console.warn('Audio play blocked/failed (non-chunked):', error);
                    trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                        failure_reason: 'existing_audio_play_failed'
                    });
                }
                return;
            } else {
                const userData = deps.storage.get('userData');
                const userTier = perkTierForUser(userData || {});
                const isGreeting = deps.isGreetingMessage(bubble.characterId);
                if (userTier === 'free' && !isGreeting && !(await isFreeVoicePaidByOpenRouter())) {
                    await showVoiceAccessModal('voice-free-tier-non-greeting', bubble);
                    trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                        failure_reason: 'free_tier_restricted'
                    });
                    bubble.setVoiceButtonState('play');
                    deps.displayManager.redrawAllMessages();
                    return;
                }

                const now = Date.now();
                if (now - _lastVoiceClickTime < _voiceClickDebounceMs) {
                    trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                        failure_reason: 'debounced'
                    });
                    return;
                }
                if (_voiceGenerationInProgress) {
                    trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                        failure_reason: 'generation_in_progress'
                    });
                    return;
                }
                _lastVoiceClickTime = now;

                const messageId = await findMessageIdByText(bubble.characterId, bubble.text);
                if (_pendingVoiceRequests.has(requestKey)) {
                    trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                        failure_reason: 'pending_request_exists'
                    });
                    return;
                }
                stopAllAudioGlobally();
                if (bubble && typeof bubble.setVoiceButtonState === 'function') {
                    bubble.setVoiceButtonState('loading');
                } else {
                    bubble.voiceButtonState = 'loading';
                    bubble.markDirty?.();
                }
                deps.displayManager.redrawAllMessages();
                _voiceGenerationInProgress = true;
                const voicePromise = playVoiceForBubble(bubble.characterId, bubble.text, bubble, messageId, voiceSettings, requestKey);
                /** @type {any} */ (voicePromise)._timestamp = Date.now();
                _pendingVoiceRequests.set(requestKey, voicePromise);
                try { await voicePromise; } finally { _voiceGenerationInProgress = false; _pendingVoiceRequests.delete(requestKey); }
            }
        } catch (error) {
            console.error('Error handling voice button click:', error);
            const errorMessage = error?.message || '';
            const hasAuthError = errorMessage.includes('Authentication required')
                || error?.status === 401
                || error?.workerError?.status === 401;
            const shouldShowUpsellModal = errorMessage === 'VOICE_QUOTA_HANDLED' || hasAuthError;
            if (errorMessage === 'QUOTA_HANDLED') {
                bubble.setVoiceButtonState('play');
            } else if (shouldShowUpsellModal) {
                await showVoiceAccessModal('voice-request-blocked', bubble);
                bubble.setVoiceButtonState('play');
            } else {
                bubble.setVoiceButtonState('error');
            }
            trackVoiceAnalyticsEvent('voice_fail', bubble.characterId, voiceSettings, {
                failure_reason: errorMessage === 'QUOTA_HANDLED'
                    ? 'voice_quota_handled'
                    : (shouldShowUpsellModal ? 'voice_request_blocked' : 'voice_button_error')
            });
            _voiceGenerationInProgress = false;
            if (requestKey) _pendingVoiceRequests.delete(requestKey);
        }
    };

    const playVoiceForBubble = async (characterId, messageText, bubble, messageId = null, voiceSettings = null, existingRequestKey = null) => {
        const settings = voiceSettings || getVoiceSettingsSnapshot(String(characterId));
        const requestKey = existingRequestKey || buildVoiceCompositeKey(characterId, messageText, settings);
        try {
            if (_pendingVoiceRequests.has(requestKey)) return;
            stopAllAudioGlobally();
            const cacheKey = getAudioCacheKey(characterId, messageText, settings);
            const audioKey = requestKey;
            const isChatbotVoiceButton = bubble?.voiceButtonElement?.classList?.contains('chatbot-message-voice-btn') === true;
            const playChatbotVoiceNow = async (audio) => {
                if (!isChatbotVoiceButton || typeof audio?.play !== 'function') return;
                try {
                    await audio.play();
                } catch (error) {
                    console.warn('Chatbot voice playback failed:', error);
                    bubble.setVoiceButtonState('ready');
                    bubble.isAudioPlaying = false;
                    deps.displayManager.redrawAllMessages();
                }
            };
            const cachedBase64 = getCachedAudio(cacheKey);
            if (cachedBase64) {
                const audio = createAudioFromBase64(cachedBase64, characterId, messageText, null, settings, audioKey);
                setupBubbleAudioListeners(audio, bubble, audioKey);
                if (isChatbotVoiceButton) {
                    audioPlayerMap.set(audioKey, { audio });
                }

                if (bubble) {
                    bubble.setVoiceButtonState('ready');
                    deps.displayManager.redrawAllMessages();
                }
                trackVoiceAnalyticsEvent('voice_success', characterId, settings, {
                    source: 'cached_audio'
                });
                await playChatbotVoiceNow(audio);
                return;
            }
            const emotion = resolveEmotionId(characterId);
            const requestBody = await prepareVoiceRequestBody(characterId, messageText, messageId, emotion);
            await maybeShowExpensiveVoiceToast({
                characterId,
                characterName: bubble?.characterName,
                voiceSettings: settings,
            });
            const apiCallPromise = callVoiceGenerationAPI(requestBody);
            /** @type {any} */ (apiCallPromise)._timestamp = Date.now();
            _pendingVoiceRequests.set(requestKey, apiCallPromise);
            const responseData = await apiCallPromise;
            if (bubble) { bubble.setVoiceButtonState('ready'); deps.displayManager.redrawAllMessages(); }
            const responseReceivedTime = performance.now();
            if (responseData.error) throw new Error(`Voice generation error: ${responseData.error}`);
            if (!responseData.audioUrl && !responseData.audio) throw new Error('No audio data received from server');
            const { audio, audioPlayer } = createAudioFromResponse(responseData, characterId, messageText, cacheKey, bubble, settings, audioKey);
            if (audioPlayer) audioPlayer.responseReceivedTime = responseReceivedTime;
            setupBubbleAudioListeners(audio, bubble, audioKey);

            if (bubble) {
                bubble.setVoiceButtonState('ready');
                deps.displayManager.redrawAllMessages();
            }
            trackVoiceAnalyticsEvent('voice_success', characterId, settings, {
                source: 'generated_audio'
            });
            await playChatbotVoiceNow(audio);
            _pendingVoiceRequests.delete(requestKey);
        } catch (error) {
            console.error('Error playing voice for bubble:', error);
            _pendingVoiceRequests.delete(requestKey);
            trackVoiceAnalyticsEvent('voice_fail', characterId, settings, {
                failure_reason: 'generation_failed'
            });
            throw error;
        }
    };

    const setupBubbleAudioListeners = (audio, bubble, audioKey) => {
        if (!bubble) return;
        audio.addEventListener('play', () => {
            bubble.setVoiceButtonState('playing');
            bubble.isAudioPlaying = true;
            deps.displayManager.redrawAllMessages();
        });
        audio.addEventListener('pause', () => {
            bubble.setVoiceButtonState('ready');
            bubble.isAudioPlaying = false;
            deps.displayManager.redrawAllMessages();
        });
        audio.addEventListener('ended', () => {
            bubble.setVoiceButtonState('ready');
            bubble.isAudioPlaying = false;
            deps.displayManager.redrawAllMessages();
        });
        audio.addEventListener('error', () => {
            audioPlayerMap.delete(audioKey);
            bubble.setVoiceButtonState('error');
            deps.displayManager.redrawAllMessages();
        });
    };

    const playVoice = async (characterId, messageText, button, messageId = null, useStreaming = true, voiceSettingsOverride = null) => {
        try {
            const voiceSettings = voiceSettingsOverride || getVoiceSettingsSnapshot(String(characterId));
            const cacheKey = getAudioCacheKey(characterId, messageText, voiceSettings);
            const cachedBase64 = getCachedAudio(cacheKey);
            if (cachedBase64) {
                const audioKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
                const audio = createAudioFromBase64(cachedBase64, characterId, messageText, button, voiceSettings, audioKey);
                if (button) button.dataset.voiceKey = audioKey;

                // Two-step voice UX: first click loads/caches and arms; second click plays.
                button.innerHTML = '<i class="fas fa-play"></i>';
                button.title = 'Play voice';
                button.disabled = false;
                setButtonBackground(button, 'rgba(255, 255, 255, 0.9)');
                trackVoiceAnalyticsEvent('voice_success', characterId, voiceSettings, {
                    source: 'cached_dom_audio'
                });
                return;
            }
            if (!messageText || messageText.trim() === '') {
                throw new Error('No message text provided for voice generation');
            }

            button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
            button.disabled = true;
            button.title = 'Generating voice...';
            setButtonBackground(button, 'rgba(255, 255, 255, 0.6)');
            const emotion = resolveEmotionId(characterId);
            const requestBody = await prepareVoiceRequestBody(characterId, messageText, messageId, emotion);
            let responseData;
            let hasStartedStreaming = false;
            await maybeShowExpensiveVoiceToast({
                characterId,
                voiceSettings,
            });
            if (useStreaming) {
                const onStreamStart = (data) => {
                    button.innerHTML = '<i class="fas fa-play"></i>';
                    const sequentialChars = data.sequentialTextLength || Math.round(data.progress * data.text_cleaned?.length || 0);
                    button.title = `Streaming... ${Math.round(data.progress * 100)}% ready (${sequentialChars} chars from start)`;
                    setButtonBackground(button, 'rgba(0, 255, 0, 0.6)');
                    hasStartedStreaming = true;

                    const activeBubbles = deps.displayManager.getActiveMessages();
                    const bubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                    if (bubble) {
                        bubble.setVoiceButtonState('ready');
                        deps.displayManager.redrawAllMessages();
                    }
                };

                const onProgress = (data) => {
                    if (button) {
                        const sequentialChars = data.sequentialTextLength || 0;
                        const totalChars = data.totalTextLength || 0;
                        const progressPercent = Math.round(data.progress * 100);
                        const sequentialChunks = data.sequentialChunks || 0;
                        if (data.canStartStreaming && !hasStartedStreaming) onStreamStart(data);
                        else if (hasStartedStreaming) button.title = `Streaming... ${progressPercent}% ready (${sequentialChars}/${totalChars} chars, ${sequentialChunks}/${data.total_chunks} sequential chunks)`;
                        else button.title = `Preparing... (${data.ready_chunks}/${data.total_chunks} chunks ready, waiting for ${data.hasApiResponse ? 'sequential order' : 'API response'})`;
                    }
                };

                try {
                    responseData = await callStreamingVoiceGenerationAPI(requestBody, onProgress, onStreamStart);
                }
                catch (streamError) {
                    console.warn('Streaming failed, falling back to regular generation:', streamError);
                    responseData = await callVoiceGenerationAPI(requestBody);
                }
            } else {
                responseData = await callVoiceGenerationAPI(requestBody);
            }
            const responseReceivedTime = performance.now();
            const audioKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
            const { audio, audioUrl, audioPlayer } = createAudioFromResponse(responseData, characterId, messageText, cacheKey, null, voiceSettings, audioKey);
            if (audioPlayer) audioPlayer.responseReceivedTime = responseReceivedTime;
            const characterColor = await deps.getCharacterColor(characterId);
            setupDOMButtonAudioListeners(audio, button, audioKey, audioUrl, characterColor);
            if (button) button.dataset.voiceKey = audioKey;

            const isGreeting = deps.isGreetingMessage(characterId);

            if (isGreeting && useStreaming && hasStartedStreaming) {
                const hangoutConfig = await deps.storageManager.getItem('currentHangoutConfig');
                const activeCharacters = hangoutConfig?.allCharacters || [];
                const character = activeCharacters.find(c => String(c.id) === String(characterId));
                const bubble = character?.bubble;

                if (bubble && audioPlayer) {
                    bubble.currentVoiceKey = audioKey;

                    const activeBubbles = deps.displayManager.getActiveMessages();
                    const activeBubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                    if (activeBubble && activeBubble !== bubble) {
                        activeBubble.currentVoiceKey = audioKey;
                    }

                    audioPlayer.onPlay = () => {
                        bubble.setVoiceButtonState('playing');
                        bubble.isAudioPlaying = true;
                        deps.displayManager.redrawAllMessages();
                    };
                    audioPlayer.onPause = () => {
                        bubble.setVoiceButtonState('ready');
                        bubble.isAudioPlaying = false;
                        deps.displayManager.redrawAllMessages();
                    };
                    audioPlayer.onEnded = () => {
                        bubble.setVoiceButtonState('ready');
                        bubble.isAudioPlaying = false;
                        deps.displayManager.redrawAllMessages();
                        if (bubble.currentVoiceKey === audioKey) {
                            bubble.currentVoiceKey = null;
                        }
                        const activeBubbles = deps.displayManager.getActiveMessages();
                        const activeBubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                        if (activeBubble && activeBubble.currentVoiceKey === audioKey) {
                            activeBubble.currentVoiceKey = null;
                        }
                    };
                }
            }

            // Two-step voice UX: first click generates and arms; second click plays.
            button.innerHTML = '<i class="fas fa-play"></i>';
            button.title = 'Play voice';
            button.disabled = false;
            setButtonBackground(button, 'rgba(255, 255, 255, 0.9)');
            trackVoiceAnalyticsEvent('voice_success', characterId, voiceSettings, {
                source: 'generated_dom_audio'
            });
        } catch (error) {
            console.error('Error playing voice:', error);

            if (error.message === 'QUOTA_HANDLED') {
                button.innerHTML = '<i class="fas fa-volume-up"></i>';
                button.title = 'Voice generation (quota exceeded)';
                button.disabled = true;
                setButtonBackground(button, 'rgba(255, 255, 255, 0.3)');
                const voiceSettings = voiceSettingsOverride || getVoiceSettingsSnapshot(String(characterId));
                trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                    failure_reason: 'voice_quota_handled'
                });
                return;
            }

            if (error.message === 'VOICE_QUOTA_HANDLED') {
                button.innerHTML = '<i class="fas fa-volume-up"></i>';
                button.title = 'Voice generation (quota exceeded)';
                button.disabled = true;
                setButtonBackground(button, 'rgba(255, 255, 255, 0.3)');
                const voiceSettings = voiceSettingsOverride || getVoiceSettingsSnapshot(String(characterId));
                trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                    failure_reason: 'voice_quota_handled'
                });
                return;
            }
            if (await presentOpenRouterVoiceKeyError(error)) {
                button.innerHTML = '<i class="fas fa-exclamation-triangle"></i>';
                button.title = error.message;
                button.disabled = false;
                setButtonBackground(button, 'rgba(255, 69, 0, 0.9)');
                const voiceSettings = voiceSettingsOverride || getVoiceSettingsSnapshot(String(characterId));
                trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                    failure_reason: error.code
                });
                return;
            }
            button.innerHTML = '<i class="fas fa-exclamation-triangle"></i>';
            button.title = 'Voice error - click to retry';
            button.disabled = false;
            setButtonBackground(button, 'rgba(255, 69, 0, 0.9)');
            const isGreeting = deps.isGreetingMessage(characterId);
            const errorMessage = error.message === 'Authentication required' && !isGreeting ? 'Please log in to use voice features' : 'Voice generation failed. Please try again.';
            console.warn('Voice error:', errorMessage);
            const voiceSettings = voiceSettingsOverride || getVoiceSettingsSnapshot(String(characterId));
            trackVoiceAnalyticsEvent('voice_fail', characterId, voiceSettings, {
                failure_reason: error.message === 'Authentication required' ? 'authentication_required' : 'dom_voice_generation_failed'
            });
        }
    };

    const generateAutoVoiceSync = async (characterId, messageText, characterName, messageId = null, emotion = null) => {
        const voiceSettings = getVoiceSettingsSnapshot(String(characterId));
        const requestKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
        try {
            if (!messageText || messageText.trim() === '') return null;
            const isAutoVoiceEnabled = deps.storage.get('autoVoiceEnabled') !== 'false';
            if (!isAutoVoiceEnabled) return null;
            const authToken = (await window.tokenManager?.getValidAccessToken?.() ?? '') || '';
            const isGreeting = deps.isGreetingMessage(characterId);
            if (!authToken && !isGreeting) return null;
            const cacheKey = getAudioCacheKey(characterId, messageText, voiceSettings);
            if (_pendingVoiceRequests.has(requestKey)) return null;
            cleanupStalePendingRequests();
            if (voicePlayerCache.has(requestKey)) {
                const cachedBase64 = voicePlayerCache.get(requestKey);
                const audio = createAudioFromBase64(cachedBase64, characterId, messageText, null, voiceSettings, requestKey);
                setupAutoVoiceEventListeners(audio, characterId, messageText, characterName || '', null);
                return { audio, audioUrl: null, cached: true };
            }
            const requestBody = await prepareVoiceRequestBody(characterId, messageText, messageId, emotion);

            let hasStartedStreaming = false;
            const onStreamStart = (data) => {
                hasStartedStreaming = true;

                const activeBubbles = deps.displayManager.getActiveMessages();
                const bubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                if (bubble) {
                    bubble.setVoiceButtonState('ready');
                    deps.displayManager.redrawAllMessages();
                }
            };

            const onProgress = (data) => { };

            await maybeShowExpensiveVoiceToast({
                characterId,
                characterName,
                voiceSettings,
            });
            const apiCallPromise = callStreamingVoiceGenerationAPI(requestBody, onProgress, onStreamStart);
            /** @type {any} */ (apiCallPromise)._timestamp = Date.now();
            _pendingVoiceRequests.set(requestKey, apiCallPromise);
            const responseData = await apiCallPromise;
            if (responseData.error) throw new Error(`Voice generation error: ${responseData.error}`);
            if (!responseData.audioUrl && !responseData.audio) throw new Error('No audio data received from server');
            const { audio, audioUrl, audioPlayer } = createAudioFromResponse(responseData, characterId, messageText, cacheKey, null, voiceSettings, requestKey);
            const bubble = deps.displayManager.getActiveMessages().find(b => b.characterId === characterId && b.text === messageText);
            if (bubble) { bubble.setVoiceButtonState('ready'); deps.displayManager.redrawAllMessages(); }
            const voiceStateKey = buildVoiceReadyKey(characterId, messageText, voiceSettings);
            try { deps.storageMechanism.setItem(voiceStateKey, 'ready'); } catch (e) { }
            if (responseData.audio) voicePlayerCache.set(requestKey, responseData.audio);
            if (isGreeting) {
                const evt = new CustomEvent('greetingAudioReady', { detail: { characterId, conversationId: deps.storage.get('currentConversationId') || null } });
                document.dispatchEvent(evt);

                const hangoutConfig = await deps.storageManager.getItem('currentHangoutConfig');
                const activeCharacters = hangoutConfig?.allCharacters || [];
                const character = activeCharacters.find(c => String(c.id) === String(characterId));
                const greetingBubble = character?.bubble;

                if (greetingBubble && audioPlayer) {
                    greetingBubble.currentVoiceKey = requestKey;

                    const activeBubbles = deps.displayManager.getActiveMessages();
                    const activeBubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                    if (activeBubble && activeBubble !== greetingBubble) {
                        activeBubble.currentVoiceKey = requestKey;
                    }

                    audioPlayer.onPlay = () => {
                        greetingBubble.setVoiceButtonState('playing');
                        greetingBubble.isAudioPlaying = true;
                        deps.displayManager.redrawAllMessages();
                    };
                    audioPlayer.onPause = () => {
                        greetingBubble.setVoiceButtonState('ready');
                        greetingBubble.isAudioPlaying = false;
                        deps.displayManager.redrawAllMessages();
                    };
                    audioPlayer.onEnded = () => {
                        greetingBubble.setVoiceButtonState('ready');
                        greetingBubble.isAudioPlaying = false;
                        deps.displayManager.redrawAllMessages();
                        if (greetingBubble.currentVoiceKey === requestKey) {
                            greetingBubble.currentVoiceKey = null;
                        }
                        const activeBubbles = deps.displayManager.getActiveMessages();
                        const activeBubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                        if (activeBubble && activeBubble.currentVoiceKey === requestKey) {
                            activeBubble.currentVoiceKey = null;
                        }
                    };

                    try {
                        if (audioPlayer.isPrepared && audioPlayer.audioBuffer && audioPlayer.chunkTimings) {
                            await audioPlayer.playWithSeparation(audioPlayer.audioBuffer, audioPlayer.chunkTimings, audioPlayer.separationMs || 500);
                        } else {
                            await audio.play();
                        }
                    } catch (error) {
                        console.warn('Auto-play greeting failed:', error);
                        if (greetingBubble) {
                            greetingBubble.setVoiceButtonState('ready');
                            greetingBubble.currentVoiceKey = null;
                            deps.displayManager.redrawAllMessages();
                        }
                        const activeBubbles = deps.displayManager.getActiveMessages();
                        const activeBubble = activeBubbles.find(b => String(b.characterId) === String(characterId));
                        if (activeBubble) {
                            activeBubble.currentVoiceKey = null;
                        }
                    }
                }
            }
            _pendingVoiceRequests.delete(requestKey);
            return { audio, audioUrl, audioPlayer, cached: false };
        } catch (error) {
            _pendingVoiceRequests.delete(requestKey);
            if (error.message === 'QUOTA_HANDLED' || error.message === 'VOICE_QUOTA_HANDLED') return null;
            if (await presentOpenRouterVoiceKeyError(error)) return null;
            console.warn('generateAutoVoiceSync failed:', error);
            return null;
        }
    };

    const generateAutoVoice = async (characterId, messageText, characterName, messageId = null, emotion = null) => {
        const voiceSettings = getVoiceSettingsSnapshot(String(characterId));
        const requestKey = buildVoiceCompositeKey(characterId, messageText, voiceSettings);
        try {
            const autoVoiceEnabled = deps.storage.get('autoVoiceEnabled') !== 'false';
            if (!autoVoiceEnabled) return null;
            if (!messageText || messageText.trim() === '') { console.warn('No message text for auto voice generation'); return null; }
            const cacheKey = getAudioCacheKey(characterId, messageText, voiceSettings);
            const cachedBase64 = getCachedAudio(cacheKey);
            if (cachedBase64) {
                const allVoiceButtons = document.querySelectorAll(`.voice-play-btn[data-character-id="${characterId}"]`);
                const voiceButton = allVoiceButtons[allVoiceButtons.length - 1];
                const audio = createAudioFromBase64(cachedBase64, characterId, messageText, voiceButton, voiceSettings, requestKey);
                try { await audio.play(); } catch (e) { console.warn('Cached auto voice autoplay blocked:', e); }
                return;
            }
            const authToken = (await window.tokenManager?.getValidAccessToken?.() ?? '') || '';
            const isGreeting = deps.isGreetingMessage(characterId);
            if (!authToken && !isGreeting) { console.warn('No auth token for auto voice generation (non-greeting)'); return; }
            if (_pendingVoiceRequests.has(requestKey)) return null;
            cleanupStalePendingRequests();
            stopAllAudioGlobally();
            const requestBody = await prepareVoiceRequestBody(characterId, messageText, messageId, emotion);
            await maybeShowExpensiveVoiceToast({
                characterId,
                characterName,
                voiceSettings,
            });
            const apiCallPromise = callStreamingVoiceGenerationAPI(requestBody);
            /** @type {any} */ (apiCallPromise)._timestamp = Date.now();
            _pendingVoiceRequests.set(requestKey, apiCallPromise);
            const responseData = await apiCallPromise;
            const responseReceivedTime = performance.now();
            const { audio, audioUrl, audioPlayer } = createAudioFromResponse(responseData, characterId, messageText, cacheKey, null, voiceSettings, requestKey);
            if (audioPlayer) audioPlayer.responseReceivedTime = responseReceivedTime;
            setupAutoVoiceEventListeners(audio, characterId, messageText, characterName, audioUrl);
            try {
                await audio.play();
                _pendingVoiceRequests.delete(requestKey);
                return audio;
            } catch (autoplayError) {
                if (autoplayError.name === 'NotAllowedError') {
                    window.pendingVoiceAudio = audio;
                    _pendingVoiceRequests.delete(requestKey);
                    return audio;
                }
                throw autoplayError;
            }
        } catch (error) {
            _pendingVoiceRequests.delete(requestKey);
            if (error.message === 'QUOTA_HANDLED' || error.message === 'VOICE_QUOTA_HANDLED') return null;
            if (await presentOpenRouterVoiceKeyError(error)) return null;
            console.warn('Error in auto voice generation:', error);
            return null;
        }
    };

    return {
        init,
        // Public API used by wrappers
        addVoicePlayListener,
        handleVoiceButtonClick,
        playVoiceForBubble,
        playVoice,
        generateAutoVoice,
        generateAutoVoiceSync,
        restoreVoiceButtonState,
        stopAllAudioGlobally,
        stopAllAudio,
        invalidateCachedVoiceForCharacter,
        syncCachedVoiceAfterMessageEdit,
        getVoiceSettingsSnapshot,
        getCurrentAudioForCharacter,
        getAudioEntryByKey,
        // Also expose selected helpers used by wrappers or other modules
        getCurrentVoiceLanguage,
        getCurrentVoiceId,
        buildVoiceCompositeKey,
        buildVoiceReadyKey,
        getAudioCacheKey,
        cacheAudio,
        getCachedAudio,
        createAudioFromBase64,
        setupDOMButtonAudioListeners,
        createAudioFromResponse,
        setupAutoVoiceEventListeners
    };
})();
